import { minimalTimezoneSet, sessionStorageKeys } from 'constants/common'

export const BOOKING_DISPLAY_TIMEZONE_KEY =
  sessionStorageKeys.BOOKING_DISPLAY_TIMEZONE

type StoredTimezone =
  | {
      id: string
      isGeo?: false
    }
  | {
      id: string
      tzCode: string
      tzAbbr: string
      tzName: string
      offset: string
      isGeo: true
    }

export const generateTimezoneId = (timezone: TimeZone): string => {
  return `mtz-${timezone.tzCode
    .toLowerCase()
    .replace(/\//g, '-')}-${timezone.tzAbbr.toLowerCase()}`
}

/**
 * Save timezone to sessionStorage (per-tab isolation)
 * @param timezoneRow - Full TimeZone object from minimalTimezoneSet or dynamic geo zone
 */
export const saveTimezone = (timezoneRow: TimeZone): void => {
  if (typeof window === 'undefined') {
    // Never write during SSR
    return
  }

  try {
    const existingInSet = minimalTimezoneSet.find(
      (tz) =>
        tz.tzCode === timezoneRow.tzCode && tz.tzAbbr === timezoneRow.tzAbbr
    )

    let payload: StoredTimezone

    if (existingInSet) {
      payload = {
        id: generateTimezoneId(existingInSet),
      }
    } else {
      payload = {
        id: `geo-${timezoneRow.tzCode.toLowerCase().replace(/\//g, '-')}`,
        tzCode: timezoneRow.tzCode,
        tzAbbr: timezoneRow.tzAbbr,
        tzName: timezoneRow.tzName,
        offset: timezoneRow.offset,
        isGeo: true,
      }
    }

    sessionStorage.setItem(
      BOOKING_DISPLAY_TIMEZONE_KEY,
      JSON.stringify(payload)
    )
  } catch {
    /* empty */
  }
}

/**
 * Read and validate timezone from sessionStorage (per-tab isolation)
 * @returns TimeZone object or null if invalid/missing
 */
export const readTimezone = (): TimeZone | null => {
  if (typeof window === 'undefined') {
    // Never read during SSR
    return null
  }

  try {
    const stored = sessionStorage.getItem(BOOKING_DISPLAY_TIMEZONE_KEY)

    if (!stored) {
      return null
    }

    const parsed: StoredTimezone = JSON.parse(stored)

    if (!parsed || typeof parsed !== 'object' || !parsed.id) {
      sessionStorage.removeItem(BOOKING_DISPLAY_TIMEZONE_KEY)
      return null
    }

    if (parsed.isGeo) {
      // Validate offset format: +05:30 or -08:00
      const isValidOffset = /^[+-](0\d|1[0-4]):[0-5]\d$/.test(parsed.offset)

      // Validate tzCode is a real IANA timezone
      const isValidIanaTimeZone = (() => {
        try {
          new Intl.DateTimeFormat('en-US', { timeZone: parsed.tzCode })
          return true
        } catch {
          return false
        }
      })()

      if (
        typeof parsed.tzCode !== 'string' ||
        typeof parsed.tzAbbr !== 'string' ||
        typeof parsed.tzName !== 'string' ||
        typeof parsed.offset !== 'string' ||
        parsed.tzCode.trim() === '' ||
        parsed.tzAbbr.trim() === '' ||
        parsed.tzName.trim() === '' ||
        parsed.offset.trim() === '' ||
        !isValidOffset ||
        !isValidIanaTimeZone
      ) {
        sessionStorage.removeItem(BOOKING_DISPLAY_TIMEZONE_KEY)
        return null
      }

      return {
        tzCode: parsed.tzCode,
        tzAbbr: parsed.tzAbbr,
        tzName: parsed.tzName,
        offset: parsed.offset,
        label: `(GMT${parsed.offset}) ${parsed.tzName}`,
      }
    }

    const matchedById = minimalTimezoneSet.find(
      (tz) => generateTimezoneId(tz) === parsed.id
    )

    if (matchedById) {
      return matchedById
    }

    sessionStorage.removeItem(BOOKING_DISPLAY_TIMEZONE_KEY)
    return null
  } catch {
    try {
      sessionStorage.removeItem(BOOKING_DISPLAY_TIMEZONE_KEY)
    } catch {
      /* empty */
    }

    return null
  }
}

export const clearTimezone = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    sessionStorage.removeItem(BOOKING_DISPLAY_TIMEZONE_KEY)
  } catch {
    /* empty */
  }
}
